import { Avatar } from './Avatar';

/**
 * Whether somebody is here, as a dot on their face.
 *
 * ## Why on the avatar rather than beside the name
 *
 * It has to work in four places at four sizes - a 112px profile header, a 64px
 * inbox row, a 32px chat tab and a 26px window header - and the only thing all
 * four have in common is the avatar. A badge beside the name would need a
 * different position in each, and would compete with the unread count in two of
 * them.
 *
 * ## Nothing at all when they are not
 *
 * There is no grey "offline" dot. Partly because a wall of grey dots is noise
 * on a page about music, and partly because it is the privacy property: with no
 * dot, somebody who has switched this off is indistinguishable from somebody
 * who is asleep. An offline marker would make invisibility visible.
 *
 * The server has already folded the switch into `online` - see `Presence` on
 * the backend - so there is nothing to decide here.
 */
export function PresenceAvatar({
  src,
  name,
  size,
  online,
  /**
   * What the dot's ring is drawn in.
   *
   * It sits half off the edge of a round avatar, so it needs a collar in
   * whatever is behind it or it reads as a smudge. The dock is on `raised`,
   * pages are on `app`, and a wrong collar is more obvious than no dot.
   */
  ring = 'ring-app',
}: {
  src: string | null | undefined;
  name: string;
  size: number;
  online?: boolean;
  ring?: string;
}) {
  /*
   * A quarter of the face, clamped. Straight proportion gives a 28px blob on
   * the profile and something invisible on a chat tab.
   */
  const dot = Math.min(16, Math.max(9, Math.round(size / 4)));

  return (
    <span className="relative inline-flex shrink-0">
      <Avatar src={src ?? null} name={name} size={size} />

      {online && (
        <span
          data-online
          title="Online"
          style={{ height: dot, width: dot }}
          className={`absolute bottom-0 right-0 rounded-full bg-fresh ring-2 ${ring}`}
        >
          <span className="sr-only">Online</span>
        </span>
      )}
    </span>
  );
}

/**
 * The dot on its own, for the one place there is no avatar to put it on.
 *
 * The profile header draws its own avatar inside a ring that this would have to
 * reach through, so there it is positioned by the page instead.
 */
export function OnlineDot({ size = 14, ring = 'ring-app' }: { size?: number; ring?: string }) {
  return (
    <span
      data-online
      title="Online"
      style={{ height: size, width: size }}
      className={`inline-block rounded-full bg-fresh ring-2 ${ring}`}
    >
      <span className="sr-only">Online</span>
    </span>
  );
}
